(function () {
  'use strict';

  var TAG = 'dvw-review-carousel';

  if (!window.customElements || customElements.get(TAG)) return;

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  var DvwReviewCarousel = class extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;

      this.track = this.querySelector('[data-dvw-rc-track]');
      this.dotsEl = this.querySelector('[data-dvw-rc-dots]');
      this.prevBtn = this.querySelector('[data-dvw-rc-prev]');
      this.nextBtn = this.querySelector('[data-dvw-rc-next]');
      this.cards = this.track
        ? Array.prototype.slice.call(this.track.querySelectorAll('[data-dvw-rc-card]'))
        : [];

      if (!this.track || this.cards.length === 0) return;

      this._ready = true;
      this.index = 0;
      this.stopCount = 0;

      this.bindEvents();
      this.refresh();

      // Card widths depend on breakpoints and on webfonts settling, so remeasure
      // instead of trusting the first paint.
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.queueRefresh.bind(this));
        this.observer.observe(this.track);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    /* ---------- measurement ---------- */

    // Distance from one card's start to the next, gap included.
    step() {
      if (this.cards.length < 2) {
        return this.cards.length ? this.cards[0].offsetWidth : 0;
      }
      return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
    }

    maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    // One stop per reachable scroll position, not one per card. With 10 cards and
    // 3 in view there are 8 stops, so every dot moves the track and the final dot
    // is the actual end of the carousel.
    countStops() {
      var max = this.maxScroll();
      if (max <= 1) return 1;
      var step = this.step();
      if (!step) return 1;
      return clamp(Math.round(max / step) + 1, 1, this.cards.length);
    }

    indexFromScroll() {
      var max = this.maxScroll();
      if (max <= 1) return 0;

      // Treat "fully scrolled" as the last stop so the end of the track always
      // lights the last dot, whatever rounding the browser does.
      if (this.track.scrollLeft >= max - 2) return this.stopCount - 1;

      var step = this.step();
      if (!step) return 0;
      return clamp(Math.round(this.track.scrollLeft / step), 0, this.stopCount - 1);
    }

    /* ---------- rendering ---------- */

    refresh() {
      var stops = this.countStops();

      if (stops !== this.stopCount) {
        this.stopCount = stops;
        this.buildDots(stops);
      }

      this.classList.toggle('dvw-rc--static', stops <= 1);
      this.index = this.indexFromScroll();
      this.paint();
    }

    queueRefresh() {
      if (this._refreshQueued) return;
      this._refreshQueued = true;
      window.requestAnimationFrame(
        function () {
          this._refreshQueued = false;
          this.refresh();
        }.bind(this)
      );
    }

    buildDots(count) {
      if (!this.dotsEl) return;

      this.dotsEl.innerHTML = '';

      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'dvw-rc__dot';
        dot.setAttribute('aria-label', 'Go to review ' + (i + 1) + ' of ' + count);
        dot.addEventListener('click', this.goTo.bind(this, i));
        this.dotsEl.appendChild(dot);
      }

      this.dots = Array.prototype.slice.call(this.dotsEl.children);
    }

    paint() {
      if (this.dots) {
        for (var i = 0; i < this.dots.length; i++) {
          var isActive = i === this.index;
          this.dots[i].classList.toggle('is-active', isActive);
          this.dots[i].setAttribute('aria-current', isActive ? 'true' : 'false');
        }
      }

      if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index >= this.stopCount - 1;
    }

    /* ---------- movement ---------- */

    goTo(target) {
      var index = clamp(target, 0, this.stopCount - 1);
      var max = this.maxScroll();
      var left = index >= this.stopCount - 1 ? max : Math.min(max, index * this.step());

      this.index = index;
      this.paint();

      if (this.track.scrollTo) {
        this.track.scrollTo({ left: left, behavior: 'smooth' });
      } else {
        this.track.scrollLeft = left;
      }
    }

    /* ---------- events ---------- */

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener(
          'click',
          function () {
            this.goTo(this.index - 1);
          }.bind(this)
        );
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener(
          'click',
          function () {
            this.goTo(this.index + 1);
          }.bind(this)
        );
      }

      this.track.addEventListener(
        'scroll',
        function () {
          if (this._scrollQueued) return;
          this._scrollQueued = true;
          window.requestAnimationFrame(
            function () {
              this._scrollQueued = false;
              if (this.dragging) return;
              this.index = this.indexFromScroll();
              this.paint();
            }.bind(this)
          );
        }.bind(this)
      );

      this.bindDrag();
    }

    bindDrag() {
      if (!window.PointerEvent) return;

      var startX = 0;
      var startScroll = 0;
      var moved = 0;

      var onDown = function (event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (this.stopCount <= 1) return;
        if (event.target.closest('a, button')) return;

        this.dragging = true;
        moved = 0;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';
        this.track.style.cursor = 'grabbing';
      }.bind(this);

      var onMove = function (event) {
        if (!this.dragging) return;
        var delta = event.clientX - startX;
        moved = Math.abs(delta);
        if (moved > 3) event.preventDefault();
        this.track.scrollLeft = startScroll - delta;
      }.bind(this);

      var onUp = function () {
        if (!this.dragging) return;
        this.dragging = false;
        this.track.style.scrollSnapType = '';
        this.track.style.scrollBehavior = '';
        this.track.style.cursor = '';
        // Settle on the nearest stop so the dots and the track never disagree.
        this.goTo(this.indexFromScroll());
      }.bind(this);

      this.track.addEventListener('pointerdown', onDown);
      this.track.addEventListener('pointermove', onMove);
      this.track.addEventListener('pointerup', onUp);
      this.track.addEventListener('pointercancel', onUp);
      this.track.addEventListener('pointerleave', onUp);
    }
  };

  customElements.define(TAG, DvwReviewCarousel);

  // The theme editor swaps section markup in without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    var carousels = event.target.querySelectorAll(TAG);
    for (var i = 0; i < carousels.length; i++) {
      if (carousels[i].refresh) carousels[i].refresh();
    }
  });
})();
